/**
 * Keeps a persistent cart attribute `_mitgliedschaft` in sync with the total
 * value of the membership line items currently in the cart. This value shows up
 * under an order's "Additional details" (order note attributes).
 *
 * Membership products are flagged server-side through the metafield
 * `aico_metafields.mitgliedschaft-produkt`, but that flag is not exposed in the
 * AJAX cart (`/cart.js`). Every membership added through the store gets a
 * `_membership_id` line-item property (see member-info-validation.js) and every
 * renewal gets a `_renewal_group_id` (see global.js). We use those markers, plus
 * the configured membership variant ids as a fallback, to identify membership
 * lines from the AJAX cart.
 *
 * The value is stored as a real cart attribute (not just a hidden form input),
 * because logged-in customers check out through a custom draft-order app
 * (checkout-handler.js) that reads `/cart.json`. Only persisted cart attributes
 * reach that flow and, ultimately, the order's additional details. Guest
 * checkout carries cart attributes to the order as well, so a single persisted
 * attribute covers both paths.
 */
(function () {
  "use strict";

  const ATTRIBUTE_KEY = "_mitgliedschaft";

  // Flip to false to silence the verification logging in the browser console.
  const DEBUG = true;
  const log = (...args) => {
    if (DEBUG) console.log("[membership-attr]", ...args);
  };

  function getMembershipVariantIds() {
    const ids = new Set();
    if (Array.isArray(window.MembershipProductVariants)) {
      window.MembershipProductVariants.forEach((variant) => {
        if (variant && variant.id != null) ids.add(Number(variant.id));
      });
    }
    return ids;
  }

  function isMembershipItem(item, membershipVariantIds) {
    const props = item.properties || {};
    if (props._membership_id || props._renewal_group_id) return true;
    if (membershipVariantIds.has(Number(item.variant_id))) return true;
    return false;
  }

  function computeMembershipTotalCents(cart, membershipVariantIds) {
    let totalCents = 0;
    (cart.items || []).forEach((item) => {
      const isMembership = isMembershipItem(item, membershipVariantIds);
      if (isMembership) {
        // final_line_price is the price of the line (per-unit price x quantity),
        // in the shop's minor currency unit (e.g. Rappen for CHF).
        totalCents += item.final_line_price;
      }
      log(
        (isMembership ? "membership" : "skip") +
          ` line "${item.title}" variant=${item.variant_id} qty=${item.quantity}` +
          ` final_line_price=${item.final_line_price} (${(item.final_line_price / 100).toFixed(2)})` +
          ` props=${Object.keys(item.properties || {}).join(",") || "-"}`
      );
    });
    return totalCents;
  }

  function formatTotal(totalCents) {
    // The order attribute expects a currency amount, e.g. 150 (or 149.5 when
    // there are cents), not a minor-unit integer.
    return String(parseFloat((totalCents / 100).toFixed(2)));
  }

  let inFlight = false;
  let rerunRequested = false;

  async function sync() {
    if (inFlight) {
      // Make sure the latest cart state always gets a final sync.
      rerunRequested = true;
      return;
    }
    inFlight = true;

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const membershipVariantIds = getMembershipVariantIds();

      const cartResponse = await fetch(root + "cart.js", {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      const totalCents = computeMembershipTotalCents(cart, membershipVariantIds);
      const currentRaw =
        cart.attributes && cart.attributes[ATTRIBUTE_KEY] != null
          ? String(cart.attributes[ATTRIBUTE_KEY])
          : "";

      // Only write when there are memberships, or to correct a stale value left
      // behind after all memberships were removed. Non-membership carts never
      // receive the attribute.
      if (totalCents === 0 && currentRaw === "") {
        log(`no membership items in cart -> ${ATTRIBUTE_KEY} not set (skip)`);
        return;
      }

      const target = formatTotal(totalCents);
      log(
        `membership total = ${target} (${totalCents} minor units); ` +
          `current ${ATTRIBUTE_KEY}=${JSON.stringify(currentRaw)} -> target=${JSON.stringify(target)}`
      );
      if (currentRaw === target) {
        log(`${ATTRIBUTE_KEY} already up to date (skip write)`);
        return;
      }

      const updateResponse = await fetch(root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: target } }),
      });
      const updatedCart = await updateResponse.json();
      // Proof of persistence: this is the value now stored on the cart object
      // (what /cart.json returns and what the order's additional details show).
      log(
        `persisted on cart -> ${ATTRIBUTE_KEY} =`,
        updatedCart.attributes && updatedCart.attributes[ATTRIBUTE_KEY],
        "| full cart.attributes:",
        updatedCart.attributes
      );
    } catch (error) {
      console.error("membership-cart-attribute sync failed:", error);
    } finally {
      inFlight = false;
      if (rerunRequested) {
        rerunRequested = false;
        sync();
      }
    }
  }

  function init() {
    sync();

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => sync());
    }

    // The membership modal flow (member-info-validation.js) adds items through a
    // bespoke AJAX call that does not publish cartUpdate, so it dispatches this
    // event after adding, letting us re-sync before the customer checks out.
    document.addEventListener("membership:cart-updated", () => sync());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Exposed so other cart-mutating code can trigger a sync directly if needed.
  window.syncMembershipCartAttribute = sync;
})();
